import {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate} from 'react-router-dom'
import {MasterLayout} from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../pages/dashboard/DashboardWrapper'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import UserManagementWrapper from '../pages/user-management/UserManagementWrapper'
import TripManagementWrapper from '../pages/trip-management/TripManagementWrapper'
import UserDetails from '../pages/user-management/UserDetails'
import GiftCardManagementWrapper from '../pages/gift-card-management/GiftCardManagementWrapper'
import GiftCardDetails from '../pages/gift-card-management/GiftCardDetails'
import MatesManagementWrapper from '../pages/mates-management/MatesManagementWrapper'
import PlatformPricingWrapper from '../pages/platform-pricing/PlatformPricingWrapper'
import WalletWrapper from '../pages/wallet/WalletWrapper'
import SupportMessagesWrapper from '../pages/support-messages/SupportMessagesWrapper'
import FeedbackResponseWrapper from '../pages/feedback-response/FeedbackResponseWrapper'
import PaymentManagementWrapper from '../pages/payment-management/PaymentManagementWrapper'
import PaymentDetails from '../pages/payment-management/PaymentDetails'
import TripDetails from '../pages/trip-management/TripDetails'
import MatesDetails from '../pages/mates-management/MatesDetails'
import Forbidden403 from '../pages/errors/Forbidden403'
import {RequirePermission} from '../modules/auth/core/AdminPermissions'
import AdminUsersWrapper from '../pages/admin-users/AdminUsersWrapper'
import RolesWrapper from '../pages/roles/RolesWrapper'
import RoleEditor from '../pages/roles/RoleEditor'
import AuditLogsWrapper from '../pages/audit-logs/AuditLogsWrapper'
import BusinessMatrixWrapper from '../pages/business-matrix/BusinessMatrixWrapper'

const VideosPage = lazy(() => import('../pages/explore-more/VideosPage'))
const VideoFormPage = lazy(() => import('../pages/explore-more/VideoFormPage'))
const BlogsPage = lazy(() => import('../pages/explore-more/BlogsPage'))
const BlogFormPage = lazy(() => import('../pages/explore-more/BlogFormPage'))
const OffersPage = lazy(() => import('../pages/explore-more/OffersPage'))
const ImpactConfigPage = lazy(() => import('../pages/impact-config/ImpactConfigPage'))
const EmailManagementPage = lazy(() => import('../pages/email-management/EmailManagementPage'))
const TransactionalEmailsPage = lazy(
  () => import('../pages/email-management/TransactionalEmailsPage')
)

const PrivateRoutes = () => {
  const AccountPage = lazy(() => import('../modules/accounts/AccountPage'))

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path='auth/*' element={<Navigate to='/dashboard' />} />
        <Route path='403' element={<Forbidden403 />} />
        <Route
          path='dashboard'
          element={
            <RequirePermission code='dashboard.view'>
              <DashboardWrapper />
            </RequirePermission>
          }
        />
        <Route
          path='business-matrix'
          element={
            <RequirePermission code='dashboard.view'>
              <SuspensedView>
                <BusinessMatrixWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route path='revenue' element={<Navigate to='/business-matrix' replace />} />
        <Route
          path='account/*'
          element={
            <RequirePermission code='dashboard.view'>
              <SuspensedView>
                <AccountPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='user-management'
          element={
            <RequirePermission code='user_mgmt.view'>
              <SuspensedView>
                <UserManagementWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='user-management/:id'
          element={
            <RequirePermission code='user_mgmt.view'>
              <SuspensedView>
                <UserDetails />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='trip-management'
          element={
            <RequirePermission code='trip_mgmt.view'>
              <SuspensedView>
                <TripManagementWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='trip-management/:id'
          element={
            <RequirePermission code='trip_mgmt.view'>
              <SuspensedView>
                <TripDetails />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='gift-card-management'
          element={
            <RequirePermission code='gift_card_mgmt.view'>
              <SuspensedView>
                <GiftCardManagementWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='gift-card-management/:id'
          element={
            <RequirePermission code='gift_card_mgmt.view'>
              <SuspensedView>
                <GiftCardDetails />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='mates-management'
          element={
            <RequirePermission code='mates_mgmt.view'>
              <SuspensedView>
                <MatesManagementWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='mates-management/:id/*'
          element={
            <RequirePermission code='mates_mgmt.view'>
              <SuspensedView>
                <MatesDetails />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='platform-pricing'
          element={
            <RequirePermission code='platform_pricing.view'>
              <SuspensedView>
                <PlatformPricingWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='wallet-management'
          element={
            <RequirePermission code='wallet.view'>
              <SuspensedView>
                <WalletWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='support-messages'
          element={
            <RequirePermission code='feedback.view'>
              <SuspensedView>
                <SupportMessagesWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='feedback-response'
          element={
            <RequirePermission code='feedback.view'>
              <SuspensedView>
                <FeedbackResponseWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='payment'
          element={
            <RequirePermission code='payment_mgmt.view'>
              <SuspensedView>
                <PaymentManagementWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='payment/:id'
          element={
            <RequirePermission code='payment_mgmt.view'>
              <SuspensedView>
                <PaymentDetails />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route path='explore-more' element={<Navigate to='/explore-more/videos' replace />} />
        <Route
          path='explore-more/videos'
          element={
            <RequirePermission code='explore_more.view'>
              <SuspensedView>
                <VideosPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='explore-more/videos/new'
          element={
            <RequirePermission code='explore_more.create'>
              <SuspensedView>
                <VideoFormPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='explore-more/videos/:id/edit'
          element={
            <RequirePermission code='explore_more.edit'>
              <SuspensedView>
                <VideoFormPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='explore-more/blogs'
          element={
            <RequirePermission code='explore_more.view'>
              <SuspensedView>
                <BlogsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='explore-more/blogs/new'
          element={
            <RequirePermission code='explore_more.create'>
              <SuspensedView>
                <BlogFormPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='explore-more/blogs/:id'
          element={
            <RequirePermission code='explore_more.edit'>
              <SuspensedView>
                <BlogFormPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='explore-more/offers'
          element={
            <RequirePermission code='explore_more.view'>
              <SuspensedView>
                <OffersPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='impact-config'
          element={
            <RequirePermission code='impact_config.view'>
              <SuspensedView>
                <ImpactConfigPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='email-management'
          element={
            <RequirePermission code='email_management.view'>
              <SuspensedView>
                <EmailManagementPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='email-management/transactional'
          element={
            <RequirePermission code='email_management.view'>
              <SuspensedView>
                <TransactionalEmailsPage />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='admin-users'
          element={
            <RequirePermission code='admin_users.view'>
              <SuspensedView>
                <AdminUsersWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='settings/roles'
          element={
            <RequirePermission code='roles.view'>
              <SuspensedView>
                <RolesWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='settings/roles/:id'
          element={
            <RequirePermission code='roles.view'>
              <SuspensedView>
                <RoleEditor />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route
          path='audit-logs'
          element={
            <RequirePermission code='audit_logs.view'>
              <SuspensedView>
                <AuditLogsWrapper />
              </SuspensedView>
            </RequirePermission>
          }
        />
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {PrivateRoutes}
